/**
 * Browser telemetry bootstrap (Next.js instrumentation-client convention).
 * Runs once before the app becomes interactive. No-op unless
 * NEXT_PUBLIC_APPINSIGHTS_CONNECTION_STRING is set at build time.
 */
import { registerAppInsights, trackPageView } from "@/lib/telemetry";

const connectionString = process.env.NEXT_PUBLIC_APPINSIGHTS_CONNECTION_STRING;

if (connectionString) {
  // Errors thrown before the dynamic import resolves would otherwise vanish:
  // buffer them, replay into trackException once the SDK registers, then drop
  // the temporary listeners (the SDK installs its own from that point on).
  const buffered: unknown[] = [];
  const onError = (e: ErrorEvent) => {
    buffered.push(e.error ?? e.message);
  };
  const onRejection = (e: PromiseRejectionEvent) => {
    buffered.push(e.reason);
  };
  const removeBufferListeners = () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
  try {
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
  } catch {
    /* telemetry must never break the app */
  }

  import("@microsoft/applicationinsights-web")
    .then(({ ApplicationInsights }) => {
      const appInsights = new ApplicationInsights({
        config: {
          connectionString,
          autoTrackPageVisitTime: true,
          // Route changes are tracked via onRouterTransitionStart below.
          enableAutoRouteTracking: false,
          enableUnhandledPromiseRejectionTracking: true,
        },
      });
      appInsights.loadAppInsights();
      registerAppInsights(appInsights);
      appInsights.trackPageView(); // initial load
      try {
        removeBufferListeners();
        for (const reason of buffered.splice(0)) {
          appInsights.trackException({
            exception: reason instanceof Error ? reason : new Error(String(reason)),
            properties: { boundary: "pre-sdk-buffer" },
          });
        }
      } catch {
        /* ignore */
      }
    })
    .catch(() => {
      /* telemetry must never break the app */
      removeBufferListeners();
    });
}

export function onRouterTransitionStart(url: string) {
  trackPageView(url);
  // Drive the global NavigationProgress bar — server-component navigations
  // can take a beat before the destination renders, and the user needs to
  // see that the click landed.
  window.dispatchEvent(new CustomEvent("rb:nav-start", { detail: url }));
}
